// ─────────────────────────────────────────────────────────────────────────────
// EVERY TOP-LEVEL VIEW, AND WHICH WORKSPACE IT BELONGS TO.
//
// App.js picks the page from a `filter` value: one `{filter === "X" && ...}` block
// per view. Nothing tied the names that code navigates TO (setFilter('X'), the
// notification and nudge maps, the onboarding steps) to the names that RENDER, so
// six places sent people to 'Invoicing', a view with no block: an empty page. This
// list is that tie. navTargets.test.js fails if a render block is missing from it,
// if it names a view with no block, or if anything navigates to a name that is not
// here and available somewhere.
//
// `workspaces` is where the view belongs, mirroring the App.js sidebar: a Business
// page is reachable in a Personal workspace only by mistake, because most Business
// blocks are not gated on the workspace. "Personal" means type === 'personal',
// exactly as App.js:382 derives isPersonalWorkspace; every other type gets the
// Business layout.
// ─────────────────────────────────────────────────────────────────────────────

export const BUSINESS = 'business';
export const PERSONAL = 'personal';
const BOTH = [BUSINESS, PERSONAL];

export const VIEWS = {
  AideyChat:      { workspaces: BOTH },
  Dashboard:      { workspaces: BOTH },
  Tasks:          { workspaces: BOTH },
  Today:          { workspaces: BOTH },
  Tomorrow:       { workspaces: BOTH },
  'Week View':    { workspaces: BOTH },
  List:           { workspaces: BOTH },
  Notes:          { workspaces: BOTH },
  EmailDashboard: { workspaces: BOTH },
  Contacts:       { workspaces: BOTH },
  ProjectTracker: { workspaces: BOTH },
  Notifications:  { workspaces: BOTH },
  Settings:       { workspaces: BOTH },
  Profile:        { workspaces: BOTH },
  AdminPanel:     { workspaces: BOTH },

  Insights:       { workspaces: [BUSINESS] },
  Financials:     { workspaces: [BUSINESS] },
  Invoices:       { workspaces: [BUSINESS] },
  CRM:            { workspaces: [BUSINESS] },
  Forms:          { workspaces: [BUSINESS] },
  Workflows:      { workspaces: [BUSINESS] },
  Proposals:      { workspaces: [BUSINESS] },
  Contracts:      { workspaces: [BUSINESS] },
  Scheduler:      { workspaces: [BUSINESS] },
  ClientPortal:   { workspaces: [BUSINESS] },
  TeamDashboard:  { workspaces: [BUSINESS] },
  TeamCalendar:   { workspaces: [BUSINESS] },
  TeamHR:         { workspaces: [BUSINESS] },
  TeamManagement: { workspaces: [BUSINESS] },
  Timesheet:      { workspaces: [BUSINESS] },
  Calendar:       { workspaces: [BUSINESS] },   // App.js renders it only when !isPersonalWorkspace

  Family:         { workspaces: [PERSONAL] },
  FamilyChat:     { workspaces: [PERSONAL] },
  Budget:         { workspaces: [PERSONAL] },
  MealPlanner:    { workspaces: [PERSONAL] },
  Shopping:       { workspaces: [PERSONAL] },
  Renewals:       { workspaces: [PERSONAL] },
  Birthdays:      { workspaces: [PERSONAL] },
  Journal:        { workspaces: [PERSONAL] },

  // UI-2: the private UI kit at /dev/ui-kit. Admin only (isAdminRole), in no navigation:
  // not in SIDEBAR, reached by its URL. Both workspaces, so it previews either.
  UiKit:          { workspaces: BOTH },

  // Fresh-eyes item 27: what an address that is no page shows (nav/paths.js viewForPath). Never in
  // the sidebar or search; nothing navigates to it by name.
  NotFound:       { workspaces: BOTH },
};

// ─────────────────────────────────────────────────────────────────────────────
// THE DESKTOP SIDEBAR, AS DATA: what global search offers as pages (decision 20).
//
// Same order, icons, labels and visibility as the `<aside className="sidebar">` list in
// App.js. `rule` copies that list's condition for the item:
//   teamAdmin  shown unless on a Team plan without the team-admin role (TeamDashboard, HR)
//   ledger     shown to whoever the server says may see the business ledger (decision 70:
//              the owner or the finance role; someone with no business sees their own).
//              currentUser.canSeeLedger, computed once in lib/coveredMember.js
//   adminRole  shown only to the super_admin, admin and cs_manager roles (Admin panel)
// Pro and Team pages with an upgrade tag ARE shown, as the sidebar shows them; the
// page itself carries the upgrade prompt. navTargets.test.js fails if an item is
// added to or removed from the App.js sidebar without this list.
// ─────────────────────────────────────────────────────────────────────────────
// Every item's icon is its own (fresh-eyes item 22): Notes/Forms, Dashboard/Team dashboard,
// Scheduler/Team calendar, Contacts/HR and Financials/Budget each shared one. navIcons.test.js.
export const SIDEBAR = [
  { key: 'AideyChat',      icon: '🤖', labelKey: 'nav.aidey_chat' },
  { key: 'Dashboard',      icon: '📊', labelKey: 'nav.dashboard' },
  { key: 'Insights',       icon: '📈', label: 'Insights' },
  { key: 'Financials',     icon: '💰', labelKey: 'nav.financial_overview', rule: 'ledger' },
  { key: 'Tasks',          icon: '✅', labelKey: 'nav.my_tasks', badge: 'tasks' },
  // UI-4, decision 55: the Business calendar page had a render block and nothing reaching it.
  { key: 'Calendar',       icon: '🗓️', label: 'Calendar' },
  // Decision 56: Personal's Calendar opens the Family page on its calendar tab.
  { key: 'Family',         id: 'PersonalCalendar', icon: '🗓️', label: 'Calendar', deep: { entity: 'family_tab', id: 'calendar' } },
  { key: 'Family',         icon: '👨‍👩‍👧‍👦', label: 'Family' },
  { key: 'FamilyChat',     icon: '💬', label: 'Family Chat' },
  { key: 'Budget',         icon: '👛', label: 'Budget' },
  { key: 'EmailDashboard', icon: '✉️', labelKey: 'nav.emails', badge: 'emails' },
  { key: 'ProjectTracker', icon: '🗂', labelKey: 'nav.projects', pro: 'pro' },
  { key: 'Invoices',       icon: '🧾', labelKey: 'nav.invoicing' },   // UI-24 item 19: in Basics (10 sent a month), so not marked PRO
  { key: 'Notes',          icon: '📝', labelKey: 'nav.notes' },
  { key: 'Contacts',       icon: '👥', labelKey: 'nav.contacts' },
  { key: 'CRM',            icon: '📋', labelKey: 'nav.crm', pro: 'pro' },
  { key: 'Forms',          icon: '☑️', labelKey: 'nav.forms', pro: 'pro' },
  { key: 'Workflows',      icon: '⚡', label: 'Workflows', pro: 'pro' },
  { key: 'Proposals',      icon: '📄', labelKey: 'nav.proposals', pro: 'pro' },
  { key: 'Contracts',      icon: '✍️', labelKey: 'nav.contracts', pro: 'pro' },
  { key: 'Scheduler',      icon: '📅', labelKey: 'nav.scheduler', pro: 'pro' },
  { key: 'ClientPortal',   icon: '🌐', labelKey: 'nav.client_portal', pro: 'pro' },
  { key: 'MealPlanner',    icon: '🍽️', label: 'Meal Planner' },
  { key: 'Shopping',       icon: '🛒', label: 'Shopping' },
  { key: 'Renewals',       icon: '🔁', label: 'Renewals' },
  { key: 'Birthdays',      icon: '🎂', label: 'Birthdays & Anniversaries' },
  { key: 'Journal',        icon: '📓', label: 'Journal' },
  { key: 'TeamDashboard',  icon: '🏢', labelKey: 'nav.team_dashboard', rule: 'teamAdmin', team: true, pro: 'team' },
  { key: 'TeamCalendar',   icon: '📆', labelKey: 'nav.team_calendar', team: true, pro: 'team' },
  { key: 'TeamHR',         icon: '📇', label: 'HR', rule: 'teamAdmin', team: true, pro: 'team' },
  { key: 'Timesheet',      icon: '⏱', labelKey: 'nav.timesheet', pro: 'pro' },
  { key: 'Settings',       icon: '⚙️', labelKey: 'nav.settings' },
  { key: 'AdminPanel',     icon: '🛡️', label: 'Admin Panel', rule: 'adminRole' },
];

// An item's own identity: its view, unless one view has two entries (Personal's Calendar).
export const itemId = (item) => item.id || item.key;
const byId = Object.fromEntries(SIDEBAR.map((i) => [itemId(i), i]));

// ─────────────────────────────────────────────────────────────────────────────
// THE GROUPS (UI-4): brief section 4 for Business, decisions 11, 56 and 57 for Personal.
// The sidebar, the phone drawer, search and the tests all read these, so the desktop and the
// phone can never list different things again. Each group's colour is a token name.
// ─────────────────────────────────────────────────────────────────────────────
export const SIDEBAR_GROUPS = {
  [BUSINESS]: [
    { key: 'overview',   label: 'Overview',        color: 'group-overview',   items: ['Dashboard', 'Insights', 'Financials'] },
    { key: 'work',       label: 'My work',         color: 'group-my-work',    items: ['Tasks', 'Calendar', 'EmailDashboard', 'ProjectTracker', 'Notes'] },
    { key: 'clients',    label: 'Clients & sales', color: 'group-clients',    items: ['CRM', 'Contacts', 'Proposals', 'Contracts', 'Invoices', 'Scheduler', 'ClientPortal'] },
    { key: 'automation', label: 'Automation',      color: 'group-automation', items: ['Forms', 'Workflows'] },
    { key: 'team',       label: 'Team',            color: 'group-team',       items: ['TeamDashboard', 'TeamCalendar', 'TeamHR', 'Timesheet'] },
  ],
  [PERSONAL]: [
    { key: 'overview', label: 'Overview', color: 'group-overview', items: ['Dashboard'] },
    { key: 'stuff',    label: 'My stuff', color: 'group-my-work',  items: ['Tasks', 'PersonalCalendar', 'Notes', 'Journal', 'EmailDashboard', 'Contacts', 'ProjectTracker'] },
    { key: 'family',   label: 'Family',   color: 'group-family',   items: ['Family', 'FamilyChat', 'Birthdays'] },
    { key: 'home',     label: 'Home',     color: 'group-home',     items: ['MealPlanner', 'Shopping'] },
    { key: 'money',    label: 'Money',    color: 'group-money',    items: ['Budget', 'Renewals'] },
  ],
};
export const SIDEBAR_FOOTER = ['Settings', 'AdminPanel'];

const ADMIN_ROLES = ['super_admin', 'admin', 'cs_manager'];

// The Admin panel's role check, for anything else that is admin only (the /dev/ui-kit page).
export function isAdminRole(role) {
  return ADMIN_ROLES.includes(String(role || '').toLowerCase());
}

// Does this person see this item here? (the workspace, then the item's rule)
function shows(item, { isPersonal, hasTeamPlanAccess, userIsTeamAdmin, role, canSeeLedger }) {
  if (!item || !VIEWS[item.key].workspaces.includes(isPersonal ? PERSONAL : BUSINESS)) return false;
  if (item.rule === 'teamAdmin') return !hasTeamPlanAccess || !!userIsTeamAdmin;
  if (item.rule === 'ledger') return !!canSeeLedger;
  if (item.rule === 'adminRole') return ADMIN_ROLES.includes(String(role || '').toLowerCase());
  return true;
}

// The groups this person sees here, each with only the items they see; an empty group is dropped.
export function sidebarGroups(who) {
  return SIDEBAR_GROUPS[who.isPersonal ? PERSONAL : BUSINESS]
    .map((g) => ({ ...g, items: g.items.map((id) => byId[id]).filter((item) => shows(item, who)) }))
    .filter((g) => g.items.length > 0);
}

export function sidebarFooter(who) {
  return SIDEBAR_FOOTER.map((id) => byId[id]).filter((item) => shows(item, who));
}

// The sidebar items this person sees in this workspace, in sidebar order: Aidey, the groups,
// then the footer. Search offers exactly these (decision 12).
export function sidebarViews(who) {
  return [byId.AideyChat, ...sidebarGroups(who).flatMap((g) => g.items), ...sidebarFooter(who)];
}

// Where choosing an item goes: its view, and any deep link (Personal's Calendar → the Family
// page's calendar tab).
export function navTarget(id) {
  const item = byId[id];
  return item ? { view: item.key, deep: item.deep || null } : { view: id, deep: null };
}

// The active workspace as a three-state flag: true (personal), false (any other
// type), or null while the workspace list has not loaded. Null matters: a cold-start
// push can navigate before workspaces arrive, and judging that moment as "Business"
// would wrongly turn a Personal user's Family Chat link into the fallback.
export function personalFlag(workspaces, activeWorkspaceId) {
  const ws = (workspaces || []).find(w => w.id === activeWorkspaceId);
  return ws ? ws.type === PERSONAL : null;
}

// Can this view be shown in the active workspace? Unknown views never; an unknown
// workspace (isPersonal === null) is not judged, so the caller's choice stands.
export function isViewAvailable(view, isPersonal) {
  const entry = Object.prototype.hasOwnProperty.call(VIEWS, view) ? VIEWS[view] : null;
  if (!entry || entry.workspaces.length === 0) return false;
  if (isPersonal === null || isPersonal === undefined) return true;
  return entry.workspaces.includes(isPersonal ? PERSONAL : BUSINESS);
}

// Where a notification may take you: its own section when that section exists in the
// workspace you are in, otherwise the Notifications page, which always does, and
// where the notification itself is listed. Covers notification types that do not
// exist yet as well as today's cross-workspace ones.
export function notificationView(view, isPersonal) {
  return isViewAvailable(view, isPersonal) ? view : 'Notifications';
}
